import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './app/App.tsx';
import { applyTheme, useTheme } from './theme/useTheme.ts';
import './theme/themes.css';
import './styles.css';

// Apply before the first paint. index.html also sets this inline, so a
// reload never flashes the default theme before the chosen one loads.
applyTheme(useTheme.getState().theme);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // TVs and phones suspend aggressively; refetching on every focus makes
      // returning to the app feel busy rather than instant.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60_000,
      /*
       * A DAY IN MEMORY, not TanStack's default five minutes. With five, a
       * screen nobody had visited for five minutes was thrown away and came
       * back as a spinner — which on a television left on the library is
       * every screen, every time. Staleness still decides when to refresh;
       * this only decides whether there is anything to show meanwhile. See
       * also lib/persist.ts, which carries the same data across reloads.
       */
      gcTime: 24 * 60 * 60_000,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
